import * as Slider from '@radix-ui/react-slider';
import { SPECIALTY_LABEL, SPECIALTY_TOKENS } from '@hc/shared';
import {
  CollapsibleFilterRow,
  FilterModalShell,
  MultiSelectList,
  summarizeSelection,
  type SelectOption,
} from './filter-modal-kit.js';
import {
  MAP_FILTER_DEFS,
  defaultMapFilters,
  formatMapRevenueBound,
  isMapRevenueActive,
  labelForFacet,
  summarizeMapRevenue,
  type MapFilterKey,
  type MapFilters,
  type RevenueRange,
} from './map-filters.js';
import { REVENUE_STOPS, nearestStopIndex } from './revenue-scale.js';
import { useGlobalCurrency } from './store.js';
import type { GlobalCurrency } from '../currency.js';
import type { FacilitiesFacets } from '../api/client.js';

// Facilities Map filter modal — country / sub-segment / ownership (multi-
// select) + an EUR revenue range. Built from the shared filter-modal-kit so
// it renders in the same visual language as the Screener and Operating
// Pressure filters.

export function MapFilterModal({
  open,
  onOpenChange,
  filters,
  facets,
  onChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: MapFilters;
  facets: FacilitiesFacets | undefined;
  onChange: (next: MapFilters) => void;
}) {
  const optionsFor = (key: MapFilterKey): SelectOption[] => {
    const def = MAP_FILTER_DEFS.find((d) => d.key === key)!;
    const values = facets ? facets[def.facetKey] : [];
    // Sub-segment is hierarchical: /facets returns SUBSEGMENT_FILTER_OPTIONS —
    // each canonical-17 parent followed by its fine specialty children — so the
    // children are indented and given their display label, exactly as in the
    // Screener. Everything else is a flat facet list.
    return values.map((v) =>
      key === 'subsegment' && SPECIALTY_TOKENS.has(v)
        ? { value: v, label: SPECIALTY_LABEL[v] ?? v, depth: 1 }
        : { value: v, label: labelForFacet(key, v) },
    );
  };

  const setFor = (key: MapFilterKey, next: Set<string>) => {
    onChange({ ...filters, [key]: next });
  };

  const revenue = filters.revenue;
  const currency = useGlobalCurrency();

  return (
    <FilterModalShell
      open={open}
      onOpenChange={onOpenChange}
      title="Map filters"
      resetLabel="Reset"
      doneLabel="Done"
      onReset={() => onChange(defaultMapFilters())}
    >
      {MAP_FILTER_DEFS.map((def) => {
        const selected = filters[def.key];
        return (
          <CollapsibleFilterRow
            key={def.key}
            label={def.label}
            active={selected.size > 0}
            summary={summarizeSelection(selected, (v) => labelForFacet(def.key, v))}
          >
            <MultiSelectList
              options={optionsFor(def.key)}
              selected={selected}
              onChange={(next) => setFor(def.key, next)}
            />
          </CollapsibleFilterRow>
        );
      })}

      <CollapsibleFilterRow
        label="Revenue estimate"
        active={isMapRevenueActive(revenue)}
        summary={summarizeMapRevenue(revenue, currency)}
      >
        <RevenueSlider
          value={revenue}
          currency={currency}
          onChange={(next) => onChange({ ...filters, revenue: next })}
        />
      </CollapsibleFilterRow>
    </FilterModalShell>
  );
}

function RevenueSlider({
  value,
  currency,
  onChange,
}: {
  value: RevenueRange;
  currency: GlobalCurrency;
  onChange: (next: RevenueRange) => void;
}) {
  // Non-linear axis: the slider runs over REVENUE_STOPS indices (each stop
  // equal track width), so small companies fill most of the track.
  const lo = nearestStopIndex(value[0]);
  const hi = nearestStopIndex(value[1]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Slider.Root
        value={[lo, hi]}
        min={0}
        max={REVENUE_STOPS.length - 1}
        step={1}
        onValueChange={(vs) => {
          if (vs.length === 2)
            onChange([REVENUE_STOPS[vs[0]!]!, REVENUE_STOPS[vs[1]!]!] as RevenueRange);
        }}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          userSelect: 'none',
          touchAction: 'none',
          width: '100%',
          height: 20,
        }}
      >
        <Slider.Track
          style={{
            position: 'relative',
            flexGrow: 1,
            background: 'var(--hairline)',
            height: 3,
            borderRadius: 2,
          }}
        >
          <Slider.Range
            style={{ position: 'absolute', background: 'var(--accent)', height: '100%' }}
          />
        </Slider.Track>
        {[0, 1].map((i) => (
          <Slider.Thumb
            key={i}
            style={{
              display: 'block',
              width: 12,
              height: 12,
              background: 'var(--surface)',
              border: '2px solid var(--accent)',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          />
        ))}
      </Slider.Root>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--t-body)',
          color: 'var(--text-secondary)',
        }}
      >
        <span>{formatMapRevenueBound(REVENUE_STOPS[lo]!, 'min', currency)}</span>
        <span>{formatMapRevenueBound(REVENUE_STOPS[hi]!, 'max', currency)}</span>
      </div>
    </div>
  );
}
